<template>
  <button class="btn" @click="openModal">
    <i class="fas fa-edit text-base-content/95"></i> Cập nhật chiến dịch
  </button>
  <dialog ref="dialogRef" class="modal">
    <div class="modal-box">
      <h3 class="text-lg font-bold">Cập nhật thông tin chiến dịch</h3>
      <form @submit="onSubmit">
        <div class="space-y-4 py-4">
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Tên chiến dịch</legend>
            <input
              v-model="campaignName"
              type="text"
              class="input input-bordered w-full"
              :class="{ 'input-error': errors.campaignName }"
              placeholder="Nhập tên chiến dịch"
            />
            <p v-if="errors.campaignName" class="label text-error">
              {{ errors.campaignName }}
            </p>
          </fieldset>

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Mô tả chiến dịch</legend>
            <textarea
              v-model="description"
              class="textarea textarea-bordered w-full"
              :class="{ 'textarea-error': errors.description }"
              placeholder="Mô tả chiến dịch của bạn"
            ></textarea>
            <p v-if="errors.description" class="label text-error">
              {{ errors.description }}
            </p>
          </fieldset>
        </div>
        <div class="modal-action">
          <button type="button" class="btn" @click="closeModal">Đóng</button>
          <button
            type="submit"
            class="btn btn-primary ml-2 min-w-28"
            :disabled="!hasChanges || isUpdating"
          >
            <span v-if="isUpdating">
              <i class="fa-solid fa-spinner fa-spin"></i>
            </span>
            <span v-else> Cập nhật </span>
          </button>
        </div>
      </form>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { updateCampaign } from "@/api/campaign";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useForm } from "vee-validate";
import { computed, ref, watch } from "vue";
import { toast } from "vue3-toastify";

interface CampaignData {
  id: string | number;
  name: string;
  description?: string;
}

interface Props {
  campaign: CampaignData;
}

const props = defineProps<Props>();

const { defineField, handleSubmit, errors, resetForm, setValues } = useForm({
  validationSchema: {
    campaignName: "required|maxLength:500",
    description: "maxLength:1000",
  },
});

const [campaignName] = defineField("campaignName", { label: "Tên chiến dịch" });
const [description] = defineField("description", { label: "Mô tả" });

const dialogRef = ref<HTMLDialogElement | null>(null);
const queryClient = useQueryClient();

const originalValues = ref<CampaignData>({
  id: "",
  name: "",
  description: "",
});

// Mutation for updating campaign
const { mutate: doUpdateCampaign, isPending: isUpdating } = useMutation({
  mutationFn: async (data: { name: string; description?: string }) => {
    return await updateCampaign(String(props.campaign.id), data);
  },
  onSuccess: () => {
    toast.success("Cập nhật chiến dịch thành công!");
    queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    closeModal();
  },
  onError: (error) => {
    console.error(error);
    toast.error("Đã xảy ra lỗi khi cập nhật chiến dịch.");
  },
});

const hasChanges = computed(() => {
  return (
    campaignName.value !== originalValues.value.name ||
    (description.value || "") !== (originalValues.value.description || "")
  );
});

const openModal = () => {
  const initialValues = {
    campaignName: props.campaign.name,
    description: props.campaign.description || "",
  };

  setValues(initialValues);

  originalValues.value = {
    id: props.campaign.id,
    name: props.campaign.name,
    description: props.campaign.description || "",
  };

  dialogRef.value?.showModal();
};

const onSubmit = handleSubmit((values) => {
  if (!hasChanges.value) {
    return;
  }

  doUpdateCampaign({
    name: values.campaignName,
    description: values.description,
  });
});

const closeModal = () => {
  dialogRef.value?.close();
  resetForm();
};

// Watch for prop changes to update original values
watch(
  () => props.campaign,
  (newCampaign) => {
    if (newCampaign) {
      originalValues.value = {
        id: newCampaign.id,
        name: newCampaign.name,
        description: newCampaign.description || "",
      };
    }
  },
  { deep: true },
);
</script>
